import { TrashIcon } from '@heroicons/react/24/outline';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { logger } from '@/services/logger';

import {
    useDeleteBulkSubscriptionEntries,
    useDeleteSelectedBulkSubscriptionEntries,
} from '../bulk-subscriptions.queries';
import { BulkSubscription, BulkSubscriptionEntry } from '../bulk-subscriptions.types';

const DeleteSelectedEntriesConfirmationDialog = ({
    entity,
    entries,
    isGlobalSelection = false,
    keywords,
    totalCount,
    open,
    onClose,
}: {
    entity: BulkSubscription;
    entries: BulkSubscriptionEntry[];
    /**
     * True when the user picked "Select all" (every filtered entry) rather
     * than a handful of individual rows. When true, the dialog calls the
     * server-side `DELETE /entries/all?keywords=<filter>` endpoint instead
     * of shipping every entry key in the request body — which is both
     * cheaper on the wire and avoids preloading every page just to delete.
     */
    isGlobalSelection?: boolean;
    /**
     * Text filter that produced the current selection. Forwarded as the
     * `keywords` query param to the delete-all endpoint when
     * `isGlobalSelection` is true; ignored otherwise. Omit or pass an
     * empty string to wipe every entry.
     */
    keywords?: string;
    /**
     * Total number of entries matching the current filter. Used for the
     * confirmation copy in Select-All mode so we don't rely on the length
     * of a client-side `entries` array (which may lag behind pagination).
     */
    totalCount?: number;
    open: boolean;
    onClose: (shouldRefetchData?: boolean) => void;
}) => {

    const { toast } = useToast();
    const { t } = useTranslation();

    // In Select-All mode we trust the server-side count so the confirmation
    // copy stays accurate even before every page has been streamed in.
    const count = isGlobalSelection
        ? (totalCount ?? entries.length)
        : entries.length;
    // Anna asked to use the singular wording ("1 entry" / "1 Eintrag")
    // instead of forcing plurals onto the {{count}}=1 case. We keep the
    // formatted count with thousands separators for every other value.
    const isSingle = count === 1;
    const formattedCount = count.toLocaleString();

    const titleText = isSingle
        ? t('BULK_SUBSCRIPTION.DELETE_SINGLE_ENTRY_TITLE')
        : t('BULK_SUBSCRIPTION.DELETE_N_ENTRIES_TITLE', { count: formattedCount });

    const bodyText = isSingle
        ? t('BULK_SUBSCRIPTION.DELETE_SINGLE_ENTRY_BODY_LINE_1')
        : t('BULK_SUBSCRIPTION.DELETE_SELECTION_BODY_LINE_1', { count: formattedCount });

    const buttonText = isSingle
        ? t('BULK_SUBSCRIPTION.DELETE_SINGLE_ENTRY')
        : t('BULK_SUBSCRIPTION.DELETE_N_ENTRIES', { count: formattedCount });

    const mutationCallbacks = {
        onSuccess: () => {
            toast.success('BULK_SUBSCRIPTION.ENTRIES_DELETED_SUCCESSFULLY');
            onClose(true);
        },
        onError: (error: unknown) => {
            logger.error('DeleteSelectedEntriesConfirmationDialog: ', error);
            toast.error('ERROR_OCCURRED');
        }
    };

    const { mutate: deleteSelectedEntries, isPending: isSelectedDeletePending } =
        useDeleteSelectedBulkSubscriptionEntries(mutationCallbacks);
    const { mutate: deleteAllEntries, isPending: isAllDeletePending } =
        useDeleteBulkSubscriptionEntries(mutationCallbacks);

    const isDeletePending = isSelectedDeletePending || isAllDeletePending;

    const deleteEntities = () => {
        if (isGlobalSelection) {
            deleteAllEntries({
                bulkSubscriptionId: entity.id,
                // Empty string / undefined → server wipes every entry.
                keywords: keywords || undefined,
            });
            return;
        }

        deleteSelectedEntries({
            bulkSubscriptionId: entity.id,
            entries: entries.map(entry => entry.entryMap),
        });
    };

    return (
        <Dialog open={open} onOpenChange={() => onClose()}>
            <DialogContent
                variant="small"
                onPointerDownOutside={() => onClose()}
                onInteractOutside={() => onClose()}
            >
                <DialogHeader>
                    <DialogHeaderDefault>
                        {titleText}
                    </DialogHeaderDefault>
                </DialogHeader>

                <DialogBody padding="none" className="tw-px-5 tw-py-8 lg:tw-px-8 lg:tw-py-12 tw-flex tw-flex-col tw-gap-4">
                    <div className="tw-m-0">
                        {bodyText}
                    </div>
                    <div className="tw-m-0">
                        {t('BULK_SUBSCRIPTION.DELETE_SELECTION_BODY_LINE_2')}
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="destructive"
                        disabled={isDeletePending}
                        loading={isDeletePending}
                        onClick={deleteEntities}
                    >
                        <span className="tw-flex tw-items-center">
                            <Icon icon={TrashIcon} size="sm" variant="destructive" disabled={isDeletePending} />
                            <span className="tw-pl-1.5 tw-truncate">
                                {buttonText}
                            </span>
                        </span>
                    </Button>
                    <DialogClose asChild={true}>
                        <Button variant="secondary" onClick={() => onClose()} disabled={isDeletePending}>
                            {t('CLOSE')}
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { DeleteSelectedEntriesConfirmationDialog };
